import React, { useReducer, useState, useEffect, useRef, useMemo } from "react";
import {
  roleDistributorReducer,
  initialRoleDistributorState,
} from "./RoleDistributorReducer";
import { RoleDistributorSocketService } from "./RoleDistributorSocketService";
import RoleDistributorInGameSetting from "./RoleDistributorInGameSetting";
import AbilityPhaseControl from "./AbilityPhaseControl";
import AbilityModal from "./AbilityModal";
import AbilityButton from "./AbilityButton";
import AbilityResultModal from "./AbilityResultModal";
import { getCoreActions } from "./abilities";
import { InGameViewProps } from "../GamePage";
import Chatbox from "components/Chatbox";
import ProductGameHeader from "../ProductGameHeader";
import { useTranslation } from "react-i18next";
import UserSession from "../../network/UserSession";
import { PlayerState } from "../../module/types";
import SEO from "../../components/SEO";
import "sass/views/roleDistributor/RoleDistributorView.scss";

/**
 * 역할 룰렛 메인 게임 뷰
 * - 채팅창 UI (WaitingRoomView와 유사)
 * - 방장: 라벨 세팅/게임설정/시작/종료 버튼 (모달)
 * - 유저: 본인 라벨 확인
 * - 역할배정 결과는 상단에 고정 표시
 */
const RoleDistributorView: React.FC<InGameViewProps> = ({
  product,
  room,
  messages,
  setMessages,
  onReturnToWaitingRoom,
}) => {
  const { t, i18n } = useTranslation();
  const userSession = UserSession.getInstance();
  const currentUserId = userSession.getUserId();

  const [state, dispatch] = useReducer(
    roleDistributorReducer,
    initialRoleDistributorState
  );
  const [isRuleOpen, setIsRuleOpen] = useState(false);
  const [isUserListOpen, setIsUserListOpen] = useState(false);
  const [isRoleSettingOpen, setIsRoleSettingOpen] = useState(false);
  const [isSettingOpen, setIsSettingOpen] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [isAbilityModalOpen, setIsAbilityModalOpen] = useState(false);
  const [abilityModalInitialTab, setAbilityModalInitialTab] = useState<
    "abilities" | "results"
  >("abilities");
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [currentResults, setCurrentResults] = useState<any[]>([]);

  // 소켓 서비스 레퍼런스
  const socketServiceRef = useRef<RoleDistributorSocketService | null>(null);

  // 자주 사용되는 state 값들
  const {
    players,
    defaultLabel,
    labels: customLabels,
    started,
    hostReveal,
    abilityPhase,
    abilityUsages,
    pastAbilityResults,
  } = state;

  // 🎯 복잡한 계산만 useMemo 사용
  const currentPlayer = useMemo(
    () => players.find((p) => p.id === currentUserId),
    [players, currentUserId]
  );

  const totalCustomCount = useMemo(
    () => customLabels.reduce((sum, l) => sum + l.count, 0),
    [customLabels]
  );

  // ⚡ 단순한 값들은 직접 계산
  const isHost = currentPlayer?.isHost || false;
  const myLabel = currentPlayer?.label;
  // 사회자 모드일 때 방장 제외하여 계산
  const eligiblePlayerCount = hostReveal ? players.length - 1 : players.length;
  const defaultCount = eligiblePlayerCount - totalCustomCount;

  // 게임에 존재하는 모든 역할 목록 (see_roles 액션용)
  const allAvailableRoles = useMemo(() => {
    const roleNames: string[] = [];
    if (defaultLabel?.name) {
      roleNames.push(defaultLabel.name);
    }
    customLabels.forEach((label) => {
      if (label.name) roleNames.push(label.name);
    });
    return roleNames;
  }, [defaultLabel, customLabels]);

  // 소켓 서비스 초기화 및 cleanup
  useEffect(() => {
    // 소켓 서비스 초기화
    socketServiceRef.current = new RoleDistributorSocketService(dispatch);
    socketServiceRef.current.initializeListeners();

    return () => {
      // cleanup
      if (socketServiceRef.current) {
        socketServiceRef.current.cleanup();
      }
    };
  }, [room.roomName]);

  // 설정이 성공적으로 완료되었을 때 에러 메시지 클리어
  useEffect(() => {
    if (state.settingsCompleted && validationError) {
      setValidationError("");
    }
  }, [state.settingsCompleted, validationError]);

  // 게임 시작 시 Role Setting 모달 자동 닫기
  useEffect(() => {
    if (state.started) {
      setIsRoleSettingOpen(false);
    }
  }, [state.started]);

  // 능력 결과 표시 핸들러
  const handleShowResults = (results: any[]) => {
    setCurrentResults(results);
    setIsResultModalOpen(true);
    // 결과를 과거 결과에 저장
    dispatch({ type: "SAVE_ABILITY_RESULTS", results });
  };

  // 역할 설정 검증 및 완료 함수
  const handleCompleteSettings = async () => {
    // 1. 기본 역할 검증 (공백 제거 후 확인)
    if (!state.defaultLabel?.name || !state.defaultLabel.name.trim()) {
      setValidationError(t("RoleDistributor.enterBasicRole"));
      return;
    }

    // 2. 참여 인원 검증
    if (state.players.length === 0) {
      setValidationError(t("RoleDistributor.noParticipants"));
      return;
    }

    // 3. 특수역할 인원 수 총합 검증 (사회자 모드일 때 방장 제외)
    const totalLabelCount = state.labels.reduce((sum, l) => sum + l.count, 0);
    const eligiblePlayerCount = state.hostReveal
      ? state.players.length - 1
      : state.players.length;
    if (totalLabelCount > eligiblePlayerCount) {
      setValidationError(
        t("RoleDistributor.specialRolesExceedPlayers", {
          count: eligiblePlayerCount,
        })
      );
      return;
    }

    // 4. 소켓 서비스 확인
    if (!socketServiceRef.current) {
      setValidationError(t("RoleDistributor.serverDisconnected"));
      return;
    }

    // 서버에 설정 완료 요청
    try {
      socketServiceRef.current.completeSettings(
        room.roomName,
        state.defaultLabel,
        state.labels,
        state.hostReveal
      );
      setValidationError(""); // 성공 시 에러 메시지 클리어
    } catch (error) {
      setValidationError(t("RoleDistributor.settingsCompleteFailed"));
    }
  };

  // ==================== RENDER FUNCTIONS ====================

  // 헤더 렌더링
  const renderHeader = () => (
    <ProductGameHeader
      product={product}
      room={room}
      isRuleOpen={isRuleOpen}
      setIsRuleOpen={setIsRuleOpen}
      isUserListOpen={isUserListOpen}
      setIsUserListOpen={setIsUserListOpen}
      isSettingOpen={isSettingOpen}
      setIsSettingOpen={(value) => {
        setIsSettingOpen(value);
        // 메뉴 버튼 클릭시 역할 설정 모달 닫기
        if (value) {
          setIsRoleSettingOpen(false);
        }
      }}
      userCount={players.length}
      t={t}
      extraHeaderButtons={
        /* 역할 설정 버튼 (방장용) - 헤더에 표시 */
        isHost ? (
          <button
            onClick={() => {
              setIsRuleOpen(false);
              setIsSettingOpen(false);
              setIsRoleSettingOpen(!isRoleSettingOpen);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-800 via-teal-700/80 to-cyan-800 hover:from-blue-900 hover:via-teal-800 hover:to-cyan-900 text-white rounded-lg shadow-lg transition-all duration-200 font-medium flex-shrink-0 will-change-transform border border-teal-600/40"
            style={{ minWidth: "fit-content", minHeight: "32px" }}
          >
            <i className="fas fa-crown text-xs" />
            <span className="text-xs">
              {t("RoleDistributor.roleSettingsHost")}
            </span>
          </button>
        ) : undefined
      }
    >
      {/* Leave Room button - 설정 메뉴 안에 표시 */}
      <button
        className="w-full px-4 py-2.5 bg-gray-800 hover:bg-red-600 text-red-400 hover:text-white transition-colors font-semibold flex items-center justify-center "
        onClick={() => onReturnToWaitingRoom()}
      >
        <i className="fas fa-sign-out-alt mr-2" />
        {t("leaveRoom")}
      </button>
    </ProductGameHeader>
  );

  // 요약 상황판 렌더링 (항상 고정 표시)
  const renderSummaryPanel = () => {
    return (
      <div className="bg-gray-800/50 border-b border-gray-700 px-4 py-3">
        <div className="max-w-4xl mx-auto">
          {hostReveal ? (
            <div className="space-y-2">
              {/* 사회자 모드 타이틀 */}
              <div className="text-amber-300 font-medium text-sm flex items-center">
                <i className="fas fa-eye mr-2" />
                {t("RoleDistributor.hostMode")}
              </div>

              {/* 역할 정보 */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {/* 대기중/진행중 상태를 맨 왼쪽으로 */}
                  <div
                    className={`text-xs px-2 py-1 rounded-full ${
                      started
                        ? "bg-green-600/20 text-green-300"
                        : "bg-gray-600/20 text-gray-300"
                    }`}
                  >
                    <i
                      className={`fas ${started ? "fa-play" : "fa-pause"} mr-1`}
                    />
                    {started
                      ? t("RoleDistributor.inProgress")
                      : t("RoleDistributor.waiting")}
                  </div>

                  <div className="w-px h-4 bg-gray-600"></div>

                  <div className="text-indigo-300 font-medium text-sm">
                    {defaultLabel?.name || t("RoleDistributor.notSet")}
                    {defaultCount > 0 && `(${defaultCount})`}
                  </div>

                  {customLabels.length > 0 && (
                    <div className="flex items-center space-x-2">
                      <div className="w-px h-4 bg-gray-600"></div>
                      <div className="flex items-center space-x-1">
                        {customLabels.map((label, index) => (
                          <span
                            key={label.name}
                            className="text-purple-300 text-sm"
                          >
                            {label.name}({label.count})
                            {index < customLabels.length - 1 && ", "}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* 대기중/진행중 상태를 맨 왼쪽으로 */}
                <div
                  className={`text-xs px-2 py-1 rounded-full ${
                    started
                      ? "bg-green-600/20 text-green-300"
                      : "bg-gray-600/20 text-gray-300"
                  }`}
                >
                  <i
                    className={`fas ${started ? "fa-play" : "fa-pause"} mr-1`}
                  />
                  {started
                    ? t("RoleDistributor.inProgress")
                    : t("RoleDistributor.waiting")}
                </div>

                <div className="w-px h-4 bg-gray-600"></div>

                <div className="text-indigo-300 font-medium text-sm">
                  {defaultLabel?.name || t("RoleDistributor.notSet")}
                  {defaultCount > 0 && `(${defaultCount})`}
                </div>

                {customLabels.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <div className="w-px h-4 bg-gray-600"></div>
                    <div className="flex items-center space-x-1">
                      {customLabels.map((label, index) => (
                        <span
                          key={label.name}
                          className="text-purple-300 text-sm"
                        >
                          {label.name}({label.count})
                          {index < customLabels.length - 1 && ", "}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // 방장 공개 모드 결과 렌더링 (콤팩트)
  const renderHostRevealResults = () => {
    // 역할별 색상 결정 함수
    const getRoleColors = (playerLabel: string | undefined) => {
      if (!playerLabel || playerLabel === t("RoleDistributor.unassigned")) {
        // 사회자 (미배정 또는 호스트)
        return {
          bg: "bg-amber-900/20 border-amber-700/30",
          badge: "bg-amber-600 text-amber-50",
        };
      } else if (playerLabel === defaultLabel?.name) {
        // 기본 역할
        return {
          bg: "bg-blue-900/20 border-blue-700/30",
          badge: "bg-blue-600 text-blue-50",
        };
      } else {
        // 특수 역할
        return {
          bg: "bg-purple-900/20 border-purple-700/30",
          badge: "bg-purple-600 text-purple-50",
        };
      }
    };

    return (
      <div className="space-y-2">
        {/* Role Assignment Grid */}
        <div className="grid grid-cols-2 gap-0.5">
          {players.map((player) => {
            const colors = getRoleColors(player.label);
            const displayLabel =
              !player.label || player.label === t("RoleDistributor.unassigned")
                ? t("RoleDistributor.moderator")
                : player.label;

            // 플레이어의 액션 조회
            const playerAbilities = player.abilities || [];
            const coreActions = getCoreActions(t);
            const abilityIcons = playerAbilities
              .map((abilityId) => {
                const action = coreActions.find((a) => a.id === abilityId);
                return action ? action.icon : null;
              })
              .filter(Boolean);

            return (
              <div
                key={player.id}
                className={`flex items-center justify-between px-3 py-1.5 ${colors.bg} border rounded-md text-sm gap-3`}
              >
                <span className="text-white font-medium truncate">
                  {player.name}
                </span>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Abilities Icons */}
                  {abilityIcons.length > 0 && (
                    <div className="flex items-center gap-1 px-2 py-1 bg-black/20 rounded text-xs">
                      {abilityIcons.map((icon, index) => (
                        <span key={index} className="text-yellow-300">
                          {icon}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Role Label */}
                  <span
                    className={`px-2 py-1 ${colors.badge} rounded text-sm font-medium`}
                  >
                    {displayLabel}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // 개인 결과 렌더링 (콤팩트)
  const renderPersonalResults = () => {
    // 개인 역할별 색상 결정
    const getPersonalRoleColors = () => {
      if (!myLabel || myLabel === t("RoleDistributor.unassigned")) {
        // 사회자 (미배정)
        return {
          iconGradient: "from-amber-500 to-yellow-600",
          badgeGradient: "from-amber-600 to-yellow-600",
        };
      } else if (myLabel === defaultLabel?.name) {
        // 기본 역할
        return {
          iconGradient: "from-blue-500 to-indigo-600",
          badgeGradient: "from-blue-600 to-indigo-600",
        };
      } else {
        // 특수 역할
        return {
          iconGradient: "from-purple-500 to-pink-600",
          badgeGradient: "from-purple-600 to-pink-600",
        };
      }
    };

    const colors = getPersonalRoleColors();
    const displayLabel =
      !myLabel || myLabel === t("RoleDistributor.unassigned")
        ? t("RoleDistributor.moderator")
        : myLabel;

    return (
      <div className="text-center">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center justify-center gap-2">
            <div
              className={`w-6 h-6 bg-gradient-to-br ${colors.iconGradient} rounded-full flex items-center justify-center`}
            >
              <i className="fas fa-user-secret text-white text-xs" />
            </div>
            <div
              className={`px-2 py-1 bg-gradient-to-r ${colors.badgeGradient} text-white rounded-md font-bold text-sm`}
            >
              {displayLabel}
            </div>
          </div>

          {/* 능력 버튼과 히스토리 버튼 - 우측으로 이동 */}
          {currentPlayer && (
            <div className="flex items-center gap-2">
              <AbilityButton
                onClick={() => {
                  setAbilityModalInitialTab("abilities");
                  setIsAbilityModalOpen(true);
                }}
                abilityPhase={abilityPhase}
                currentPlayer={currentPlayer}
                abilityUsages={abilityUsages}
              />
              <button
                onClick={() => {
                  setAbilityModalInitialTab("results");
                  setIsAbilityModalOpen(true);
                }}
                className="w-8 h-8 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white rounded-lg flex items-center justify-center transition-all duration-200 shadow-lg relative"
                title={t("RoleDistributor.abilityResults", "액션 결과")}
              >
                <i className="fas fa-history text-xs" />
                {state.abilityHistory.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold border border-gray-800 shadow-lg">
                    {state.abilityHistory.length}
                  </span>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // 역할배정 결과 렌더링 (상단 고정)
  const renderRoleAssignmentResults = () => {
    if (!started) return null;

    return (
      <div className="bg-gray-800/50 border-b border-gray-700 px-4 py-2">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-center mb-1">
            <h3 className="text-xs font-semibold text-emerald-300 flex items-center">
              <i className="fas fa-clipboard-check mr-1" />
              {t("RoleDistributor.roleAssignmentResults")}
            </h3>

            {/* Ability Phase Control (Host: 시작/종료 버튼 + 타이머, Non-Host: 타이머만) */}
            <AbilityPhaseControl
              abilityPhase={abilityPhase}
              isHost={isHost}
              socketService={socketServiceRef.current}
              hostId={state.hostId}
              allPlayers={players}
              onShowResults={handleShowResults}
            />
          </div>

          {isHost && hostReveal
            ? renderHostRevealResults()
            : renderPersonalResults()}
        </div>
      </div>
    );
  };

  // 채팅창 영역 렌더링
  const renderChatSection = () => (
    <div className="flex-1 min-h-0 bg-gray-800/50">
      <div className="w-full h-full flex flex-col min-h-0">
        <Chatbox
          product={product}
          roomName={room.roomName}
          userList={players.map((player) => ({
            socketId: `role-${player.id}`,
            defaultNickName: player.name,
            nickName: player.name,
            id: player.id,
            isHost: player.isHost,
            state: PlayerState.INGAME,
            isAI: false,
          }))}
          chatList={messages}
          setChatList={setMessages}
          smallChatKey={"role-distributor"}
          isUserListOpen={isUserListOpen}
          setIsUserListOpen={setIsUserListOpen}
        />
      </div>
    </div>
  );

  // 메인 컨텐츠 렌더링
  const renderMainContent = () => (
    <div className="role-distributor-main flex-1 flex flex-col min-h-0 pt-[56px] overflow-y-auto">
      {renderSummaryPanel()}

      {renderRoleAssignmentResults()}
      {renderChatSection()}
    </div>
  );

  // ==================== MAIN RENDER ====================

  return (
    <>
      <SEO
        title={`${product.title} - Lingple`}
        description={
          i18n.language === "ko"
            ? "마피아 같은 게임에서 필요한 역할배정 도구입니다."
            : "A tool for role assignment in games like Mafia."
        }
        keywords={
          i18n.language === "ko"
            ? "역할 배정, 마피아, 역할 룰렛"
            : "role assignment, mafia, role roulette"
        }
      />
      <div className="space-gray-theme role-distributor-container flex flex-col h-screen">
        {renderHeader()}

        {/* 역할 설정 모달 - 헤더 아래에 위치 */}
        {isRoleSettingOpen && (
          <div className="absolute top-[56px] left-0 right-0 overflow-scroll p-4 flex-col flex-wrap w-full bg-neutral-800/95 rounded-lg shadow-xl z-30 border border-gray-700 text-gray-300 transform transition-all duration-200 ease-in-out max-h-[calc(100vh-60px)]">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-800 via-teal-700/80 to-cyan-800 rounded-lg flex items-center justify-center mr-3 border border-teal-600/40">
                  <i className="fas fa-crown text-white text-sm" />
                </div>
                <h2 className="text-lg font-bold text-white">
                  {t("RoleDistributor.roleSettingsHost")}
                </h2>
              </div>
              <div className="flex gap-2">
                {/* 설정 완료 전: 설정 완료 버튼 */}
                {!state.settingsCompleted && (
                  <button
                    onClick={handleCompleteSettings}
                    className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg font-medium text-xs transition-colors flex items-center gap-1"
                  >
                    <i className="fas fa-check text-xs" />
                    {t("RoleDistributor.completeSettings")}
                  </button>
                )}

                {/* 설정 완료 후: 수정하기/시작하기 또는 종료 버튼 */}
                {state.settingsCompleted && (
                  <>
                    {state.started ? (
                      <button
                        onClick={() => {
                          if (socketServiceRef.current) {
                            socketServiceRef.current.endDistribution(
                              room.roomName
                            );
                          }
                        }}
                        className="px-3 py-1.5 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white rounded-lg font-medium text-xs transition-colors flex items-center gap-1"
                      >
                        <i className="fas fa-stop text-xs" />
                        {t("RoleDistributor.endDistribution")}
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            if (socketServiceRef.current) {
                              socketServiceRef.current.resetSettings(
                                room.roomName
                              );
                            }
                          }}
                          className="px-3 py-1.5 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white rounded-lg font-medium text-xs transition-colors flex items-center gap-1"
                        >
                          <i className="fas fa-edit text-xs" />
                          {t("RoleDistributor.edit")}
                        </button>
                        <button
                          onClick={() => {
                            if (socketServiceRef.current) {
                              socketServiceRef.current.startDistribution(
                                room.roomName,
                                state.defaultLabel,
                                state.labels
                              );
                            }
                          }}
                          className="px-3 py-1.5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg font-medium text-xs transition-colors flex items-center gap-1"
                        >
                          <i className="fas fa-play text-xs" />
                          {t("RoleDistributor.start")}
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="overflow-y-auto max-h-[60vh]">
              <RoleDistributorInGameSetting
                state={state}
                dispatch={dispatch}
                room={room}
                socketService={socketServiceRef.current}
                validationError={validationError}
                setValidationError={setValidationError}
              />
            </div>
          </div>
        )}

        {renderMainContent()}

        {/* 능력 모달 */}
        {currentPlayer && (
          <AbilityModal
            isOpen={isAbilityModalOpen}
            onClose={() => setIsAbilityModalOpen(false)}
            abilityPhase={abilityPhase}
            currentPlayer={currentPlayer}
            allPlayers={players}
            abilityUsages={abilityUsages}
            pastAbilityResults={pastAbilityResults}
            abilityHistory={state.abilityHistory}
            socketService={socketServiceRef.current}
            initialTab={abilityModalInitialTab}
            seeRolesTargets={allAvailableRoles}
          />
        )}

        {/* 능력 결과 모달 */}
        <AbilityResultModal
          isOpen={isResultModalOpen}
          onClose={() => setIsResultModalOpen(false)}
          results={currentResults}
          allPlayers={players}
          currentPlayerId={currentUserId}
        />
      </div>
    </>
  );
};

export default RoleDistributorView;
